/**
 * SalespersonList Component
 *
 * Sortable list of salespeople with expandable rows, grouped into sales teams
 * (Configuration → Sales Teams) with a subtotal row per team. Teams are
 * ordered by name with Unassigned last (fixed by the backend, matching the
 * Sales Funnel card and Sales Team Report); the active sort applies to members
 * within each team. Falls back to one flat ranked list when the response
 * carries no teams.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Salesperson, SalespersonTeam, SortConfig, SortField } from './types';
import { SalespersonRow } from './SalespersonRow';
import { SalespersonTeamBlock } from './SalespersonTeamBlock';

interface SalespersonListProps {
  salespeople: Salesperson[];
  teams: SalespersonTeam[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

const SORT_COLUMNS: Array<{
  field: SortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
}> = [
  { field: 'name', label: 'Salesperson', shortLabel: 'Name', align: 'left' },
  { field: 'totalGross', label: 'Total Gross', shortLabel: 'Gross', align: 'right' },
  { field: 'totalUnits', label: 'Units', shortLabel: 'Units', align: 'right' },
];

/** Aggregation makes salespersonId unique per location, so it keys expansion. */
const rowKey = (sp: Salesperson) => `${sp.managerId}-${sp.salespersonId}`;

export const SalespersonList: React.FC<SalespersonListProps> = ({
  salespeople,
  teams,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-blue-600" />
    );
  };

  if (salespeople.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No salesperson data available for this period.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {salespeople.length} salesperson{salespeople.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="bg-white rounded-b-lg">
        {teams.length > 0
          ? teams.map((team) => (
              <SalespersonTeamBlock
                key={team.managerId || 'unassigned'}
                team={team}
                rowKey={rowKey}
                isExpanded={(sp) => expandedIds.has(rowKey(sp))}
                onToggle={(sp) => toggleExpanded(rowKey(sp))}
              />
            ))
          : salespeople.map((sp, index) => (
              <SalespersonRow
                key={rowKey(sp)}
                salesperson={sp}
                isExpanded={expandedIds.has(rowKey(sp))}
                onToggle={() => toggleExpanded(rowKey(sp))}
                rank={index + 1}
              />
            ))}
      </div>
    </div>
  );
};
